import { DICTONARY } from 'const/Dictonary';
import { format } from 'date-fns';
import { useContext, useEffect, useReducer } from 'react';
import residuosServices from 'services/residuos';
import serviceEmpresa from 'services/empresas';
import commons from 'services/commons';
import empresasService from 'services/empresas';
import residuos from 'services/residuos';
import {
  initOrdernarMatch,
  initRadioBusqueda,
  INIT_CONTEXT_MATCH
} from '../utils/initialValues';
import { MatchContext } from './MatchContext';
import { MatchReducer } from './MatchReducer';
import chatServices from 'services/chat';
import analisisTerritorial from 'services/analisisTerritorial';
import {
  ordenarDisponibilidadDistancia,
  ordenarMatchResiduos,
  ordenarPorCriterio
} from '../utils/ordenarMatch';
import { AuthContext } from 'context';
import { PERMISOS } from 'const/Permisos';
import { useMatchReducerFunctions } from './useMatchReducerFunctions';
import recursosServices from 'services/recursos';

export const MatchProvider = ({ children }) => {
  const [state, dispatch] = useReducer(MatchReducer, INIT_CONTEXT_MATCH);
  const {
    perfiles,
    isTienePermisos,
    sucursalNoAdministrada,
    sucursalSeleccionada
  } = useContext(AuthContext);

  const {
    setOrdenarMatch,
    setIsNuevosCriterios,
    setMatchFiltrado,
    setRadioBusqueda,
    setCoordenadasCentroMapa,
    setResiduoSeleccionado,
    setSucursalSeleccionado,
    setSucursalATSeleccionada,
    setSucursalUsuario,
    setTransacciones,
    setTabActiva,
    setState,
    setEmpresaSeleccionada,
    setMatchStep,
    setFiltrosAplicados,
    setSalasChat,
    setMatchAmpliado,
    setMatchEmpresas,
    setMatch,
    setIsCargandoMatchAmpliado,
    setCargandoListaSucursales,
    setCargandoListaEmpresas,
    setIsCargandoMatchEmpresas,
    setIsCargandoMatchResiduos,
    setCargandoMatch,
    setCargandoListaResiduosRecursos
  } = useMatchReducerFunctions(dispatch);

  const today = new Date();
  const todayPlusMonth = new Date(today.setMonth(today.getMonth() + 1));

  const DISABLE_TAB_RESIDUOS = !isTienePermisos(
    PERMISOS.matchAutomaticoResiduoRecurso
  );
  const DISABLE_TAB_EMPRESAS = !isTienePermisos(PERMISOS.busquedaEmpresa);

  /**
   * FUNCIONES MATCH AUTOMATICO CON NEGOCIO
   *  Función que se ejecuta por un usuario con negocio y llama a las demás funciones asociadas para efectuar un match automático.
   * @param datosUsuario los datos del usuario obtenidos en función getDatosUsuarioConNegocio
   */
  const handleCargarMatch = async datosUsuario => {
    setCargandoMatch(true);
    setState(datosUsuario);
    setMatchStep('match');
    cargarSalasChat(datosUsuario);
    await handleMatchData(datosUsuario);
  };

  const cargarSalasChat = datosUsuario => {
    //Se implementa mediante fetch ya que Axios tirá error en la configuración global.
    if (datosUsuario.tieneNegocio) {
      chatServices
        .salaChat(
          {
            totalPagina: DICTONARY.PAGINACION.ALL_DATA
          },
          DICTONARY.INTERCEPTOR.DESACTIVADO
        )
        .then(async response => {
          if (response.data.error) {
            return;
          }

          const salasResiduos = response.data.content
            .filter(sala => sala.residuo)
            .map(sucursal => {
              return {
                residuo: parseInt(sucursal.residuo.codigoResiduo),
                codigoSalaChat: sucursal.codigoSalaChat
              };
            });

          const salasEmpresas = response.data.content
            .filter(sala => sala.residuo === null)
            .map(sucursal => {
              return {
                nombreSalaChat: sucursal.nombreSalaChat.replace(
                  DICTONARY.CHAT.PREFIJO_NOMBRE_SALA_EMPRESAS,
                  ''
                ),
                codigoSalaChat: sucursal.codigoSalaChat
              };
            });

          setSalasChat({
            residuos: salasResiduos,
            empresas: salasEmpresas
          });
        })
        .catch(error => {
          setSalasChat({
            residuos: [],
            empresas: []
          });
        });
    }
  };

  /**
   * Función que ejecuta un match automático, solamente corre la primera vez que se carga la vista de match y para usuario con negocio.
   * Los usuarios sin negocio deben usar match manual.
   */
  const handleMatchData = async datosUsuario => {
    // Nuevo Request
    const request = {
      fechaDisponibilidadInicio:
        datosUsuario.criteriosMatch.fechaDisponibilidadInicio,
      fechaDisponibilidadFin: datosUsuario.criteriosMatch.fechaDisponibilidadFin
    };

    try {
      if (!DISABLE_TAB_EMPRESAS) {
        setIsCargandoMatchEmpresas(true);
        empresasService
          .matchManual(request, DICTONARY.INTERCEPTOR.DESACTIVADO)
          .then(response => {
            setMatchEmpresas({
              ...response.data
            });
          })
          .catch(error => {
            setMatchEmpresas({
              sucursales: [],
              numeroSucursales: 0
            });
          })
          .finally(() => {
            setIsCargandoMatchEmpresas(false);
          });
      }

      if (!DISABLE_TAB_RESIDUOS) {
        setIsCargandoMatchResiduos(true);
        const responseMatch = await residuos.matchManual(
          request,
          DICTONARY.INTERCEPTOR.DESACTIVADO
        );
        if (responseMatch.status === 200) {
          setMatch(ordenarMatchResiduos(responseMatch));
          setIsCargandoMatchResiduos(false);
        }
      }

      setTimeout(() => {
        setCargandoMatch(false);
      }, 5000);
    } catch (error) {
      setTimeout(() => {
        setCargandoMatch(false);
        setIsCargandoMatchResiduos(false);
      }, 5000);
      console.log(error);
    }
  };

  const handleCargarResiduosYRecursos = async codigoSucursal => {
    try {
      const { tipoResiduos, tipoRecursos } = await cargarResiduosYRecursos(
        codigoSucursal
      );
      setState({
        listaTipoResiduos: tipoResiduos,
        listaTipoRecursos: tipoRecursos
      });
    } catch (error) {
      console.error('Error al cargar los residuos y recursos:', error);
    }
  };

  const handleCargarRecursos = async codigoTipoResiduo => {
    try {
      const recursos = await cargarRecursos([codigoTipoResiduo]);
      setState({
        listaTipoRecursos: recursos
      });
    } catch (error) {
      console.error('Error al cargar los residuos y recursos:', error);
    }
  };

  const handleReCargarRecursos = async () => {
    try {
      const recursos = await cargarRecursos(
        state.listaTipoResiduos.map(item => item.codigoTipoResiduo)
      );
      setState({
        listaTipoRecursos: recursos
      });
    } catch (error) {
      console.error('Error al cargar los residuos y recursos:', error);
    }
  };

  const cargarRecursos = async listaCodigoTipoResiduo => {
    setCargandoListaResiduosRecursos(true);
    let tipoRecursos = [];

    try {
      const responseServicioRecursos =
        await recursosServices.obtenerListaRecursos({
          codigosTipoResiduo: listaCodigoTipoResiduo
        });

      if (Array.isArray(responseServicioRecursos.data)) {
        tipoRecursos = responseServicioRecursos.data;
      }
    } catch (error) {
      console.error('Error al cargar los residuos y recursos:', error);
      return [];
    } finally {
      setCargandoListaResiduosRecursos(false);
    }

    return tipoRecursos;
  };

  const cargarResiduosYRecursos = async codigoSucursal => {
    setCargandoListaResiduosRecursos(true);
    let tipoResiduos = [];
    let tipoRecursos = [];

    try {
      let params = {
        nivel: [
          DICTONARY.TIPO_RESIDUOS.NIVEL_1,
          DICTONARY.TIPO_RESIDUOS.NIVEL_2
        ]
      };

      //Es necesario mantener esta condicion para considerar a usuarios sin negocio.
      if (codigoSucursal) {
        params.codigoSucursal = codigoSucursal;
      }

      const responseServicio = await residuosServices.listaResiduos(
        params,
        DICTONARY.INTERCEPTOR.DESACTIVADO
      );

      if (Array.isArray(responseServicio.data)) {
        tipoResiduos = responseServicio.data;
      }

      const responseServicioRecursos =
        await recursosServices.obtenerListaRecursos({
          codigosTipoResiduo: tipoResiduos.map(item => item.codigoTipoResiduo)
        });

      if (Array.isArray(responseServicioRecursos.data)) {
        tipoRecursos = responseServicioRecursos.data;
      }
    } catch (error) {
      console.error('Error al cargar los residuos y recursos:', error);
    } finally {
      setCargandoListaResiduosRecursos(false);
    }

    return {
      tipoResiduos,
      tipoRecursos
    };
  };

  /**
   * Función ejecutada para la obtención de datos del usuario como residuos, tipo de recursos, sucursales, dirección de la empresa obtenidas desde la sesión del usuario.
   */
  const getDatosUsuarioConNegocio = async () => {
    try {
      let dataUsuario = {};
      let fechaDisponibilidadInicio = new Date();
      let fechaDisponibilidadFin = todayPlusMonth;

      const sucursal =
        sucursalSeleccionada.sucursal || sucursalNoAdministrada.sucursal; // TODO: VERIFICAR SI VA SER NECESARIO TENER QUE OBTENER LA SUCURSAL DE LA SESIÓN

      const { latitudDireccion, longitudDireccion } = sucursal.direccion;
      dataUsuario = {
        sucursal: sucursal,
        coordenadas: [latitudDireccion, longitudDireccion]
      };

      const { tipoResiduos, tipoRecursos } = await cargarResiduosYRecursos(
        dataUsuario.sucursal.codigoSucursal
      );

      const TAB_ACTIVA_INICIAL = isTienePermisos(
        PERMISOS.matchAutomaticoResiduoRecurso
      )
        ? DICTONARY.MATCH.TABS.RESIDUOS
        : DICTONARY.MATCH.TABS.EMPRESAS;
      const data = {
        criteriosMatch: {
          tipoRecursos: [],
          tipoResiduos: [],
          manual: false,
          isBusquedaAmpliada: false,
          fechaDisponibilidadInicio: fechaDisponibilidadInicio,
          fechaDisponibilidadFin: fechaDisponibilidadFin
        },
        sucursalUsuario: dataUsuario,
        coordenadasCentroMapa: dataUsuario.coordenadas,
        tieneNegocio: true,
        listaTipoResiduos: tipoResiduos,
        listaTipoRecursos: tipoRecursos,
        tabActiva: TAB_ACTIVA_INICIAL
      };

      return data;
    } catch (error) {
      return INIT_CONTEXT_MATCH;
    }
  };

  /**
   * FUNCIONES MATCH MANUAL SIN NEGOCIO
   * Función ejecutada al momento de cambiar los criterios del match realizando un match manual según los criterios que el usuario desea.
   * @param finalData data obtenida por servicio de match manual
   * @param formikValues valores retornados de formulario formik
   */
  const handleMatchManual = async formValues => {
    /* const { latitudDireccion, longitudDireccion } =
      DICTONARY.MAPA.DEFAULT_CORDENADAS;
    let ubicacion = [latitudDireccion, longitudDireccion]; */

    const tipoResiduos = formValues.codigosTipoResiduo
      ? [formValues.codigosTipoResiduo]
      : [];

    const tipoRecursos = formValues.codigosRecurso
      ? [formValues.codigosRecurso]
      : [];

    const TAB_ACTIVA_INICIAL = isTienePermisos(
      PERMISOS.matchAutomaticoResiduoRecurso
    )
      ? DICTONARY.MATCH.TABS.RESIDUOS
      : DICTONARY.MATCH.TABS.EMPRESAS;

    let data = {
      isNuevosCriterios: false,
      criteriosMatch: {
        tipoRecursos: tipoRecursos,
        tipoResiduos: tipoResiduos,
        manual: true,
        isBusquedaAmpliada: formValues.busquedaExtendida,
        empresa: formValues.empresa,
        sucursal: formValues.sucursal,
        fechaDisponibilidadInicio: formValues.fechaDisponibilidadInicio,
        fechaDisponibilidadFin: formValues.fechaDisponibilidadFin
      },
      matchFiltrado: {
        sucursales: [],
        numeroSucursales: 0
      },
      matchEmpresasFiltrado: {
        sucursales: [],
        numeroSucursales: 0
      },
      matchAmpliadaFiltrado: {
        sucursales: [],
        numeroSucursales: 0
      },
      radioBusqueda: initRadioBusqueda,
      ordenarMatch: initOrdernarMatch,
      empresaSeleccionada: null,
      residuoSeleccionado: null,
      sucursalSeleccionada: null,
      sucursalATSeleccionada: null,
      tabActiva: TAB_ACTIVA_INICIAL
    };

    if (formValues.sucursal && formValues.sucursal.direccion) {
      let tipoResiduosAux = state.listaTipoResiduos;
      let tipoRecursosAux = state.listaTipoRecursos;

      if (
        state.criteriosMatch.sucursal === null ||
        state.criteriosMatch.sucursal.codigoSucursal !==
          formValues.sucursal.codigoSucursal
      ) {
        const response = await cargarResiduosYRecursos(
          formValues.sucursal.codigoSucursal
        );
        tipoResiduosAux = response.tipoResiduos;
        tipoRecursosAux = response.tipoRecursos;
      }

      /* si es un match automático sin selección previa debemos cargar los datos iniciales de recursos y residuos */
      const dataUsuario = {
        sucursal: formValues.sucursal || null,
        coordenadas: [
          formValues.sucursal.direccion.latitudDireccion,
          formValues.sucursal.direccion.longitudDireccion
        ]
      };
      data.sucursalUsuario = dataUsuario;
      data.coordenadasCentroMapa = dataUsuario.coordenadas;
      data.tieneNegocio = true;
      data.matchStep = 'match';
      data.listaTipoResiduos = tipoResiduosAux;
      data.listaTipoRecursos = tipoRecursosAux;
    }

    setState(data);
  };

  /**
   * Función ejecutada para actualizar el state cuando un usuario no tiene negocio, esta función se ejecuta en la vista previaMatch establece en el state que se pase a la siguiente vista "match" y además indica en state que el usuario tieneNegocio = false
   */
  const handleMatchSinNegocio = values => {
    const { latitudDireccion, longitudDireccion } =
      DICTONARY.MAPA.DEFAULT_CORDENADAS;

    let ubicacion = [latitudDireccion, longitudDireccion];
    if (values.sucursal && values.sucursal.direccion) {
      ubicacion = [
        values.sucursal.direccion.latitudDireccion,
        values.sucursal.direccion.longitudDireccion
      ];
    }

    const dataUsuario = {
      sucursal: values.sucursal || null,
      coordenadas: ubicacion
    };

    const data = {
      sucursalUsuario: values.sucursal ? dataUsuario : null,
      coordenadasCentroMapa: dataUsuario.coordenadas,
      tieneNegocio: values.sucursal ? true : false,
      matchStep: 'match',
      isCargandoMatch: false
    };

    setState(data);
  };

  /**
   * FUNCION DE ORDENAMIENTO DE MATCH
   * Implementa la lógica de botones de ordenamiento Disponibildiad / distancia los cuales son desplegados en la BarraLateral y son utilizados para organizar el resultado del match según los criterios que el usuario desea.
   */
  useEffect(() => {
    let residuosAux = [];
    let residuoFiltradoAux = [];

    //Ambos filtros aplican orden tipo match por puntaje.
    if (
      (state.ordenarMatch.includes('distancia') &&
        state.ordenarMatch.includes('disponibilidad')) ||
      state.ordenarMatch.length === 0
    ) {
      residuosAux = ordenarDisponibilidadDistancia(state.match.residuos);
      residuoFiltradoAux = ordenarDisponibilidadDistancia(
        state.matchFiltrado.residuos
      );
    } else {
      if (state.ordenarMatch.includes('disponibilidad')) {
        residuosAux = ordenarPorCriterio(
          state.match.residuos,
          'calculoDisponibilidad'
        );
        residuoFiltradoAux = ordenarPorCriterio(
          state.matchFiltrado.residuos,
          'calculoDisponibilidad'
        );
      }

      if (state.ordenarMatch.includes('distancia')) {
        residuosAux = ordenarPorCriterio(state.match.residuos, 'distancia');
        residuoFiltradoAux = ordenarPorCriterio(
          state.matchFiltrado.residuos,
          'distancia'
        );
      }
    }

    const data = {
      match: {
        ...state.match,
        residuos: residuosAux
      },
      matchFiltrado: {
        ...state.matchFiltrado,
        residuos: residuoFiltradoAux
      }
    };

    setState(data);
  }, [state.ordenarMatch]);

  /**
   * Carga las listas iniciales para usuarios sin negocio.
   */
  const getTipodeResiduos = async () => {
    if (
      state.listaTipoResiduos &&
      state.listaTipoResiduos.length > 0 &&
      state.listaTipoRecursos &&
      state.listaTipoRecursos.length > 0
    ) {
      return;
    }

    try {
      let listaTipos = [];
      const responseTipoResiduos = await commons.listaResiduos(
        { totalPagina: 1000 },
        DICTONARY.INTERCEPTOR.DESACTIVADO
      );

      if (responseTipoResiduos.status === 200) {
        listaTipos = responseTipoResiduos.data || [];
      }

      const data = {
        listaTipoResiduos: listaTipos
      };

      setState(data);
    } catch (error) {
      setState({
        listaTipoResiduos: []
      });
    }
  };

  const getEmpresas = async () => {
    setCargandoListaEmpresas(true);
    let listaEmpresas = [];
    try {
      const response = await serviceEmpresa.obtenerListaEmpresas({
        asociados: perfiles.isSuperAdmin ? false : true
      });
      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_EMPRESAS');
      }
      listaEmpresas = response.data.map(empresa => ({
        codigoEmpresa: empresa.codigoEmpresa,
        nombreEmpresa: empresa.nombreEmpresa
      }));
    } catch (error) {
      console.error('Error al cargar las empresas:', error);
    } finally {
      setCargandoListaEmpresas(false);
    }

    setState({
      listaEmpresas: listaEmpresas
    });
  };

  const getSucursal = async codigoEmpresa => {
    setCargandoListaSucursales(true);
    const params = {
      asociados: perfiles.isSuperAdmin ? false : true,
      codigoEmpresa: codigoEmpresa
    };
    let sucursales = [];
    try {
      const response = await serviceEmpresa.obtenerListaSucursales(params);
      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_SUCURSALES');
      }
      const listaSucursales = response.data;
      const sucursalesDetalladas = await Promise.all(
        listaSucursales.map(async sucursal => {
          try {
            const responseSucursal = await serviceEmpresa.getSucursal(
              sucursal.codigoSucursal
            );
            return responseSucursal?.data || null;
          } catch {
            return null;
          }
        })
      );
      if (response.data.error) {
        throw new Error('ERROR_AL_CARGAR_SUCURSALES');
      }
      sucursales = sucursalesDetalladas;
    } catch (error) {
      console.error('Error al cargar las empresas:', error);
    } finally {
      setCargandoListaSucursales(false);
    }

    setState({
      listaSucursales: sucursales
    });
  };

  /**
   * Función ejecutada en cuadro Filtros, e implementa la lógica para filtrar el match según lo que el usuario haya seleccionado dentro de las opciones de filtro disponibles.
   */
  const handleResetFiltros = () => {
    setState({
      matchFiltrado: state.match,
      matchEmpresasFiltrado: state.matchEmpresas,
      matchAmpliadaFiltrado: state.matchAmpliada,
      residuoSeleccionado: null,
      sucursalSeleccionada: null,
      sucursalATSeleccionada: null,
      empresaSeleccionada: null,
      radioBusqueda: initRadioBusqueda
    });
  };

  /**
   * Función utilizada para usuarios sin negocio en donde presionan una empresa y se muestran los residuos en ventana popup sobre el mapa.
   * @param sucursal la sucursal asociada al market en el mapa en donde el usuario ha realizado el clic
   */
  const handleSeleccionarEmpresa = sucursal => {
    if (state.tabActiva === DICTONARY.MATCH.TABS.RESIDUOS) {
      const data = {
        empresaSeleccionada: {
          empresa: sucursal,
          residuos: state.matchFiltrado.residuos.filter(
            residuo =>
              residuo.sucursal.codigoSucursal === sucursal.codigoSucursal
          )
        }
      };
      setState(data);
    } else {
      setSucursalSeleccionado(sucursal);
    }
  };

  useEffect(() => {
    if (state.criteriosMatch.manual) {
      if (state.criteriosMatch.isBusquedaAmpliada) {
        setIsCargandoMatchAmpliado(true);
      }
      try {
        let paramServicio = {};
        if (
          state.criteriosMatch.tipoResiduos &&
          state.criteriosMatch.tipoResiduos.length > 0
        ) {
          paramServicio['codigosTipoResiduo'] =
            state.criteriosMatch.tipoResiduos[0].codigoTipoResiduo;
        }

        if (
          state.criteriosMatch.tipoRecursos &&
          state.criteriosMatch.tipoRecursos.length > 0
        ) {
          paramServicio['codigosRecurso'] =
            state.criteriosMatch.tipoRecursos[0].codigoRecurso;
        }

        if (
          state.criteriosMatch.sucursal &&
          state.criteriosMatch.sucursal.codigoSucursal
        ) {
          paramServicio['codigoSucursal'] =
            state.criteriosMatch.sucursal.codigoSucursal;
        }

        if (!DISABLE_TAB_RESIDUOS) {
          setIsCargandoMatchResiduos(true);
          residuosServices
            .matchManual(
              {
                ...paramServicio,
                fechaDisponibilidadInicio: format(
                  state.criteriosMatch.fechaDisponibilidadInicio,
                  'yyyy-MM-dd'
                ),
                fechaDisponibilidadFin: format(
                  state.criteriosMatch.fechaDisponibilidadFin,
                  'yyyy-MM-dd'
                )
              },
              DICTONARY.INTERCEPTOR.DESACTIVADO
            )
            .then(response => {
              if (response.data.error) {
                throw new Error('No hay registros');
              }
              setMatch(ordenarMatchResiduos(response));
            })
            .catch(error => {
              setMatch({
                sucursales: [],
                numeroSucursales: 0,
                numeroResiduos: 0,
                residuos: []
              });
            })
            .finally(() => {
              setIsCargandoMatchResiduos(false);
            });
        }

        if (!DISABLE_TAB_EMPRESAS) {
          setIsCargandoMatchEmpresas(true);
          empresasService
            .matchManual(
              {
                ...paramServicio,
                fechaDisponibilidadInicio: format(
                  state.criteriosMatch.fechaDisponibilidadInicio,
                  'yyyy-MM-dd'
                ),
                fechaDisponibilidadFin: format(
                  state.criteriosMatch.fechaDisponibilidadFin,
                  'yyyy-MM-dd'
                )
              },
              DICTONARY.INTERCEPTOR.DESACTIVADO
            )
            .then(response => {
              if (response.data.error) {
                throw new Error('No hay registros');
              }
              setMatchEmpresas({
                ...response.data
              });
            })
            .catch(error => {
              setMatchEmpresas({
                sucursales: [],
                numeroSucursales: 0
              });
            })
            .finally(() => {
              setIsCargandoMatchEmpresas(false);
            });
        }

        if (state.criteriosMatch.isBusquedaAmpliada) {
          analisisTerritorial
            .match(paramServicio, DICTONARY.INTERCEPTOR.DESACTIVADO)
            .then(response => {
              if (response.data.error) {
                throw new Error('No hay registros');
              }

              setMatchAmpliado({
                sucursales: response.data.empresasAT.slice(0, 30),
                numeroSucursales: response.data.numeroEmpresasAT
              });
            })
            .catch(error => {
              setMatchAmpliado({
                sucursales: [],
                numeroSucursales: 0
              });
            })
            .finally(() => {
              setIsCargandoMatchAmpliado(false);
            });
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    }
  }, [state.criteriosMatch]);

  return (
    <MatchContext.Provider
      value={{
        // States
        ...state,
        // Methods
        setOrdenarMatch,
        setIsNuevosCriterios,
        setMatchFiltrado,
        setRadioBusqueda,
        setCoordenadasCentroMapa,
        setResiduoSeleccionado,
        setSucursalSeleccionado,
        setSucursalATSeleccionada,
        setSucursalUsuario,
        setTransacciones,
        setTabActiva,
        setState,
        setEmpresaSeleccionada,
        setMatchStep,
        setFiltrosAplicados,
        getDatosUsuarioConNegocio,
        getTipodeResiduos,
        getEmpresas,
        getSucursal,

        //handlers - funciones con lógica
        handleMatchManual,
        handleMatchSinNegocio,
        handleResetFiltros,
        handleCargarMatch,
        handleSeleccionarEmpresa,
        cargarSalasChat,
        handleCargarResiduosYRecursos,
        handleCargarRecursos,
        handleReCargarRecursos,

        DISABLE_TAB_RESIDUOS,
        DISABLE_TAB_EMPRESAS
      }}
    >
      {children}
    </MatchContext.Provider>
  );
};
